import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import KonvaCanvas from './KonvaCanvas'
import styles from './IdCardForm.module.scss'
import Field from "../../models/Field";
import Font from "../../models/Font";
import Column from "../../models/Column";
import { getPlaceholderText } from './KonvaCanvas/canvasUtils';

const NEW_FIELD_DEFAULTS = {
  left: 50,
  top: 50,
  width: 125,
  height: 25,
  fontId: undefined,
  fontName: '',
  color: '#000000',
  spacing: 0,
  align: 'left',
  verticalAlign: 'middle',
  textSize: 16,
}

export default function IdCardForm({ template, imageUrl, packageId, pdfUrl, pages: initialPages = [] }) {
  const [fields, setFields] = useState({})
  const [fonts, setFonts] = useState([])
  const [allColumns, setAllColumns] = useState([])
  const [selectedFieldId, setSelectedFieldId] = useState(null)
  const [editingDefaults, setEditingDefaults] = useState({})
  const [editingCurrents, setEditingCurrents] = useState({})
  const [showFieldPanel, setShowFieldPanel] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)

  // Pages: template's own background is implicit "Page 1", TemplatePage records become Page 2+
  const isMultipage = initialPages.length > 0
  const pages = useMemo(() => {
    if (!isMultipage) return []
    // Prepend the template's own background as Page 1 (id=null → fields with templatePageId=null)
    return [
      { id: null, position: 1, imageUrl: imageUrl },
      ...initialPages.map((p, idx) => ({ ...p, position: idx + 2 }))
    ]
  }, [initialPages, imageUrl, isMultipage])
  const currentPageData = isMultipage ? pages[currentPage] : null
  const currentPageId = currentPageData ? currentPageData.id : null
  const currentImageUrl = isMultipage ? (currentPageData?.imageUrl || imageUrl) : imageUrl

  const BASE_OPTIONS = {
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
  };

  const client = new QueryClient({
    defaultOptions: {
      queries: { ...BASE_OPTIONS },
    },
  });

  // Load fields
  useEffect(() => {
    Field
      .includes(['column', 'font', 'background'])
      .where({ templateId: template.id })
      .all()
      .then((res) => {
        const fieldsData = res.data.reduce((acc, field) => {
          return (
            {...acc,
              [field.id]: {
                label: field.column.name,
                id: field.id,
                columnId: field.columnId,
                left: field.x,
                top: field.y,
                width: field.width,
                height: field.height,
                object: field,
                color: field.color,
                spacing: field.spacing,
                fontId: field.fontId,
                backgroundId: field.backgroundId,
                text: field.text,
                textSize: field.textSize,
                columnType: field.column.columnType,
                fontName: field.font && field.font.name,
                wrap: field.wrap,
                sampleText: field.sampleText,
                png: field.png,
                customized: field.customized,
                personalizationBoxUrl: field.personalizationBoxUrl,
                background: field.background,
                align: field.align,
                verticalAlign: field.verticalAlign || 'middle',
                autoFit: field.autoFit,
                minFontSize: field.minFontSize || 8,
                fontStyle: field.fontStyle || 'normal',
                textDecoration: field.textDecoration || '',
                customerEditable: field.customerEditable,
                templatePageId: field.templatePageId || null,
                horizontal: field.horizontal || false
              }
            })
        }, {})

        setFields(fieldsData)
      })
  }, [template.id])

  // Load fonts
  useEffect(() => {
    Font
      .order({ name: 'asc' })
      .per(100)
      .all()
      .then((res) => {
        setFonts(res.data.map((f) => ({ id: f.id, name: f.name, url: f.fontUrl })))
      })
  }, [])

  // Load ALL columns for this category (keep the Column objects for saving)
  useEffect(() => {
    Column
      .where({ category: template.category })
      .order({ name: 'asc' })
      .per(100)
      .all()
      .then((res) => {
        setAllColumns(res.data.map((col) => ({
          id: col.id,
          name: col.name,
          columnType: col.columnType,
          // Use database value first, then fallback to hardcoded (null check, not falsy, so "" is preserved)
          defaultPlaceholder: col.defaultPlaceholder != null ? col.defaultPlaceholder : (getPlaceholderText(col.name) || ''),
          hardcodedPlaceholder: getPlaceholderText(col.name) || '',
          object: col  // Keep the Spraypaint object for saving
        })))
      })
  }, [template.category])

  // Merge column defaultPlaceholder into fields so canvas can display it
  useEffect(() => {
    if (allColumns.length === 0) return
    const colDefaultMap = {}
    allColumns.forEach(col => { colDefaultMap[col.id] = col.defaultPlaceholder })
    setFields(prev => {
      const updated = {}
      let changed = false
      Object.entries(prev).forEach(([id, field]) => {
        const dp = colDefaultMap[field.columnId]
        if (dp !== undefined && dp !== field.defaultPlaceholder) {
          updated[id] = { ...field, defaultPlaceholder: dp }
          changed = true
        } else {
          updated[id] = field
        }
      })
      return changed ? updated : prev
    })
  }, [allColumns])

  // Filter fields for current page (multipage) or show all (single-page)
  // Fields with templatePageId=null are treated as belonging to the first page (index 0)
  const visibleFields = useMemo(() => {
    if (!isMultipage) return fields
    const filtered = {}
    Object.entries(fields).forEach(([id, field]) => {
      const belongsToCurrentPage = field.templatePageId === currentPageId ||
        (currentPage === 0 && !field.templatePageId)
      if (belongsToCurrentPage) {
        filtered[id] = field
      }
    })
    return filtered
  }, [fields, currentPageId, currentPage, isMultipage])

  // Map columns to their field(s) for the current page — Static Text can have multiple fields per column
  const columnFieldMap = useMemo(() => {
    const map = {}
    allColumns.forEach(col => {
      map[col.id] = {
        column: col,
        fields: [],
      }
    })
    Object.values(visibleFields).forEach(field => {
      if (map[field.columnId]) {
        map[field.columnId].fields.push(field)
      }
    })
    return map
  }, [allColumns, visibleFields])

  // Build flat list of rows for the field table
  // Static Text columns get one row per existing field (all with +Add visible)
  const fieldTableRows = useMemo(() => {
    const rows = []
    // Iterate allColumns (already sorted alphabetically) instead of columnFieldMap
    // to preserve alpha ordering — Object.values() sorts numeric keys by ID
    allColumns.forEach(col => {
      const entry = columnFieldMap[col.id]
      if (!entry) return
      const { column, fields: colFields } = entry
      const isStaticText = column.name === 'Static Text'

      if (isStaticText && colFields.length > 0) {
        colFields.forEach((f, idx) => {
          rows.push({
            key: `field-${f.id}`,
            column,
            field: f,
            editKey: `field_${f.id}`,
            showAdd: idx === colFields.length - 1,
            instanceNum: colFields.length > 1 ? idx + 1 : null,
          })
        })
      } else {
        const f = colFields[0] || null
        rows.push({
          key: `col-${column.id}`,
          column,
          field: f,
          editKey: column.id,
          showAdd: isStaticText || !f,
          instanceNum: null,
        })
      }
    })
    return rows
  }, [columnFieldMap])

  // Handle field selection from canvas
  const handleSelectField = useCallback((fieldId) => {
    setSelectedFieldId(fieldId)
  }, [])

  // Handle default placeholder edit
  const handleDefaultChange = useCallback((columnId, value) => {
    setEditingDefaults(prev => ({ ...prev, [columnId]: value }))
  }, [])

  // Handle current placeholder edit
  const handleCurrentChange = useCallback((columnId, value) => {
    setEditingCurrents(prev => ({ ...prev, [columnId]: value }))
  }, [])

  // Save default placeholder to Column
  const handleSaveDefault = useCallback((columnId) => {
    const entry = columnFieldMap[columnId]
    if (!entry) return

    const newDefault = editingDefaults[columnId]
    if (newDefault === undefined) return

    const colObj = entry.column.object
    if (colObj) {
      colObj.defaultPlaceholder = newDefault
      colObj.save().then(() => {
        setAllColumns(prev => prev.map(col =>
          col.id === columnId
            ? { ...col, defaultPlaceholder: newDefault }
            : col
        ))
        setEditingDefaults(prev => {
          const next = { ...prev }
          delete next[columnId]
          return next
        })
      }).catch((e) => {
        console.error('Failed to save default placeholder:', e)
        alert('Failed to save default placeholder. Check console for details.')
      })
    }
  }, [columnFieldMap, editingDefaults])

  // Save current placeholder to Field
  const handleSaveCurrent = useCallback((editKey, fieldData) => {
    if (!fieldData) return

    const newCurrent = editingCurrents[editKey]
    if (newCurrent === undefined) return

    const fieldObj = fieldData.object
    if (fieldObj) {
      // Static Text fields store content in `text`, other fields use `sampleText`
      const isStaticText = fieldData.columnType === 'static_text'
      if (isStaticText) {
        fieldObj.text = newCurrent
      } else {
        fieldObj.sampleText = newCurrent
      }
      fieldObj.save().then(() => {
        setFields(prev => ({
          ...prev,
          [fieldData.id]: {
            ...prev[fieldData.id],
            ...(isStaticText ? { text: newCurrent } : { sampleText: newCurrent })
          }
        }))
        setEditingCurrents(prev => {
          const next = { ...prev }
          delete next[editKey]
          return next
        })
      }).catch((e) => {
        console.error('Failed to save custom placeholder:', e)
        alert('Failed to save custom placeholder. Check console for details.')
      })
    }
  }, [editingCurrents])

  // Handle adding a new field
  const handleAddField = useCallback((columnId) => {
    const entry = columnFieldMap[columnId]
    if (!entry) return

    const columnName = entry.column.name
    const customCurrent = editingCurrents[columnId]
    const sampleText = customCurrent !== undefined ? customCurrent : entry.column.defaultPlaceholder

    // Offset Y position for additional fields of the same column (e.g. multiple Static Text)
    const existingCount = entry.fields.length
    const yOffset = existingCount * 30

    const field = new Field({
      x: NEW_FIELD_DEFAULTS.left,
      y: NEW_FIELD_DEFAULTS.top + yOffset,
      height: columnName.includes('Image') || columnName.includes('Picture') || columnName.includes('Pose') || columnName.includes('QR') || columnName.includes('Logo') ? 100 : NEW_FIELD_DEFAULTS.height,
      width: columnName.includes('Image') || columnName.includes('Picture') || columnName.includes('Pose') || columnName.includes('QR') || columnName.includes('Logo') ? 88 : NEW_FIELD_DEFAULTS.width,
      align: NEW_FIELD_DEFAULTS.align,
      spacing: NEW_FIELD_DEFAULTS.spacing,
      color: NEW_FIELD_DEFAULTS.color,
      fontId: columnName.includes('Barcode') ? 4 : NEW_FIELD_DEFAULTS.fontId,
      textSize: NEW_FIELD_DEFAULTS.textSize,
      templateId: template.id,
      columnId: columnId,
      sampleText: sampleText,
      templatePageId: currentPageId
    })

    field.save().then(() => {
      Field.includes(['column', 'font'])
        .find(field.id)
        .then((res) => {
          setFields(prev => ({
            ...prev,
            [res.data.id]: {
              ...NEW_FIELD_DEFAULTS,
              label: columnName,
              id: res.data.id,
              columnId: columnId,
              object: res.data,
              columnType: res.data.column?.columnType,
              sampleText: sampleText,
              fontName: columnName.includes('Barcode') ? 'Barcode' : '',
              customerEditable: res.data.customerEditable,
              templatePageId: res.data.templatePageId || null
            },
          }))
          setEditingCurrents(prev => {
            const next = { ...prev }
            delete next[columnId]
            return next
          })
        })
    }).catch((e) => {
      console.log(e)
    })
  }, [template.id, columnFieldMap, editingCurrents, currentPageId])

  // Copy all fields from Page 1 (templatePageId=null) to the current page
  const [isCopying, setIsCopying] = useState(false)
  const handleCopyFromPage1 = useCallback(() => {
    if (!currentPageId || isCopying) return

    // Get all page-1 fields (templatePageId is null)
    const page1Fields = Object.values(fields).filter(f => !f.templatePageId)
    if (page1Fields.length === 0) {
      alert('No fields on Page 1 to copy.')
      return
    }

    setIsCopying(true)
    let completed = 0

    page1Fields.forEach(srcField => {
      const field = new Field({
        x: srcField.left,
        y: srcField.top,
        height: srcField.height,
        width: srcField.width,
        align: srcField.align,
        verticalAlign: srcField.verticalAlign,
        spacing: srcField.spacing,
        color: srcField.color,
        fontId: srcField.fontId,
        textSize: srcField.textSize,
        templateId: template.id,
        columnId: srcField.columnId,
        sampleText: srcField.sampleText,
        text: srcField.text,
        wrap: srcField.wrap,
        autoFit: srcField.autoFit,
        minFontSize: srcField.minFontSize,
        fontStyle: srcField.fontStyle,
        textDecoration: srcField.textDecoration,
        customerEditable: srcField.customerEditable,
        horizontal: srcField.horizontal,
        templatePageId: currentPageId
      })

      field.save().then(() => {
        Field.includes(['column', 'font'])
          .find(field.id)
          .then((res) => {
            const f = res.data
            setFields(prev => ({
              ...prev,
              [f.id]: {
                label: f.column.name,
                id: f.id,
                columnId: f.columnId,
                left: f.x,
                top: f.y,
                width: f.width,
                height: f.height,
                object: f,
                color: f.color,
                spacing: f.spacing,
                fontId: f.fontId,
                text: f.text,
                textSize: f.textSize,
                columnType: f.column.columnType,
                fontName: f.font && f.font.name,
                wrap: f.wrap,
                sampleText: f.sampleText,
                png: f.png,
                customized: f.customized,
                personalizationBoxUrl: f.personalizationBoxUrl,
                background: f.background,
                align: f.align,
                verticalAlign: f.verticalAlign || 'middle',
                autoFit: f.autoFit,
                minFontSize: f.minFontSize || 8,
                fontStyle: f.fontStyle || 'normal',
                textDecoration: f.textDecoration || '',
                customerEditable: f.customerEditable,
                templatePageId: f.templatePageId || null
              }
            }))
            completed++
            if (completed === page1Fields.length) {
              setIsCopying(false)
            }
          })
      }).catch((e) => {
        console.error('Failed to copy field:', e)
        completed++
        if (completed === page1Fields.length) {
          setIsCopying(false)
        }
      })
    })
  }, [fields, currentPageId, template.id, isCopying])

  return (
    <QueryClientProvider client={client}>
      <div className={styles.container}>
        {isMultipage && (
          <div className={styles.pageTabs}>
            {pages.map((page, idx) => (
              <button
                key={page.id || 'page1'}
                type="button"
                className={`${styles.pageTab} ${currentPage === idx ? styles.pageTabActive : ''}`}
                onClick={() => { setCurrentPage(idx); setSelectedFieldId(null) }}
              >
                Page {page.position}
              </button>
            ))}
            {currentPage > 0 && Object.keys(visibleFields).length === 0 && (
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyFromPage1}
                disabled={isCopying}
              >
                {isCopying ? 'Copying...' : 'Copy fields from Page 1'}
              </button>
            )}
          </div>
        )}

        <KonvaCanvas
          fields={visibleFields}
          setFields={setFields}
          imageUrl={currentImageUrl}
          template={template}
          onSelectField={handleSelectField}
          fonts={fonts}
        />

        <div className={styles.fieldPanelToggle}>
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() => setShowFieldPanel(!showFieldPanel)}
          >
            {showFieldPanel ? '▼ Hide Field Manager' : '▶ Show Field Manager'}
          </button>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.pdfButton}
            >
              PDF
            </a>
          )}
        </div>

        {showFieldPanel && (
          <div className={styles.fieldManagementPanel}>
            <table className={styles.fieldTable}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Default Placeholder</th>
                  <th>Current (this template)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fieldTableRows.map(({ key, column, field, editKey, showAdd, instanceNum }) => {
                  const isOnTemplate = !!field
                  const currentPlaceholder = field ? (field.sampleText || field.text || '') : ''
                  const editDefault = editingDefaults[column.id]
                  const editCurrent = editingCurrents[editKey]

                  const displayDefault = editDefault !== undefined ? editDefault : column.defaultPlaceholder
                  const displayCurrent = editCurrent !== undefined ? editCurrent : currentPlaceholder

                  const hasDefaultChanges = editDefault !== undefined && editDefault !== column.defaultPlaceholder
                  const hasCurrentChanges = editCurrent !== undefined && editCurrent !== currentPlaceholder

                  return (
                    <tr key={key} className={isOnTemplate ? styles.onTemplate : styles.notOnTemplate}>
                      <td className={styles.fieldName}>
                        {column.name}{instanceNum ? ` #${instanceNum}` : ''}
                        {isOnTemplate && <span className={styles.onTemplateBadge}>✓</span>}
                      </td>
                      <td className={styles.placeholderCell}>
                        <div className={styles.inputGroup}>
                          <input
                            type="text"
                            value={displayDefault}
                            onChange={(e) => handleDefaultChange(column.id, e.target.value)}
                            className={styles.placeholderInput}
                          />
                          <button
                            type="button"
                            className={styles.saveButton}
                            onClick={() => handleSaveDefault(column.id)}
                            disabled={!hasDefaultChanges}
                          >
                            Save
                          </button>
                        </div>
                      </td>
                      <td className={styles.placeholderCell}>
                        {isOnTemplate && column.columnType === 'school_logo' ? (
                          <div className={styles.inputGroup}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const formData = new FormData()
                                formData.append('schoolLogo', e.target.files[0])
                                fetch(`/templates/${template.id}/fields/${field.id}/update_school_logo`,
                                  { method: 'PATCH', body: formData })
                              }}
                            />
                          </div>
                        ) : (
                          <div className={styles.inputGroup}>
                            {isOnTemplate ? (
                              <input
                                type="text"
                                value={displayCurrent}
                                onChange={(e) => handleCurrentChange(editKey, e.target.value)}
                                className={styles.placeholderInput}
                              />
                            ) : (
                              <input
                                type="text"
                                value={editCurrent !== undefined ? editCurrent : ''}
                                onChange={(e) => handleCurrentChange(editKey, e.target.value)}
                                placeholder={column.defaultPlaceholder || ''}
                                className={`${styles.placeholderInput} ${styles.placeholderInputDim}`}
                              />
                            )}
                            {isOnTemplate ? (
                              <button
                                type="button"
                                className={styles.saveButton}
                                onClick={() => handleSaveCurrent(editKey, field)}
                                disabled={!hasCurrentChanges}
                              >
                                Save
                              </button>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className={styles.actionCell}>
                        {showAdd && (
                          <button
                            type="button"
                            className={styles.addButton}
                            onClick={() => handleAddField(column.id)}
                          >
                            +Add
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </QueryClientProvider>
  )
}
